<template>
    <article class="photographer-application-card" data-role-card="photographer">
        <div class="photographer-card-art" aria-hidden="true">
            <img class="photographer-card-portrait" :src="role.portrait" alt="" />
        </div>
        <div class="photographer-card-content">
            <header class="photographer-card-topline">
                <div>
                    <p>PHOTOGRAPHER</p>
                    <h3>{{ role.professionalTypeDescription }}</h3>
                </div>
                <div class="photographer-card-badges">
                    <span v-if="hasCampusCertification" class="photographer-campus-badge">校园</span>
                    <span class="photographer-status-badge" :class="statusPresentation.className">{{ statusPresentation.label }}</span>
                </div>
            </header>

            <p class="photographer-card-description">{{ cardDescription }}</p>

            <div class="photographer-card-action-row">
                <button
                    type="button"
                    class="photographer-card-action"
                    :class="{ 'photographer-card-action--quiet': cardAction === 'detail' }"
                    @click="handleCardAction">{{ cardActionLabel }}</button>
            </div>
        </div>

        <photographer-application-dialog
            :visible="dialogVisible"
            @close="dialogVisible = false"
            @submitted="handleApplicationSubmitted" />

        <professional-application-detail-dialog
            professional-type="photographer"
            :visible="detailDialogVisible"
            :loading="detailLoading"
            :error="detailError"
            :detail="applicationDetail"
            :show-resubmit="canResubmit"
            @close="closeApplicationDetail"
            @resubmit="openResubmit" />
    </article>
</template>

<script>
import request from "@/tool/request";
import API from "@/tool/api";
import {
    normalizeProfessionalApplicationDetail,
} from "@/tool/creatorCenter.cjs";
import {
    getResultData,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";
import PhotographerApplicationDialog from "./PhotographerApplicationDialog";
import ProfessionalApplicationDetailDialog from "./ProfessionalApplicationDetailDialog";

export default {
    name: "PhotographerApplicationCard",
    components: {
        PhotographerApplicationDialog,
        ProfessionalApplicationDetailDialog,
    },
    props: {
        role: {
            type: Object,
            required: true,
        },
        hasCampusCertification: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            applicationDetail: null,
            detailLoading: false,
            detailError: "",
            detailDialogVisible: false,
            dialogVisible: false,
        };
    },
    computed: {
        initialApplication() {
            return this.role.initialApplication || null;
        },
        initialApplicationId() {
            return this.initialApplication && this.initialApplication.id;
        },
        statusPresentation() {
            const status = Number(this.role.currentStatus);
            if (status === 1) return { label: "已认证", className: "approved" };
            if (status === 2) return { label: "已暂停", className: "review" };
            if (status === 3) return { label: "后台停用", className: "withdrawn" };
            return { label: "未生效", className: "ready" };
        },
        cardAction() {
            const actions = {
                SUBMIT_INITIAL_APPLICATION: "apply",
                VIEW_INITIAL_APPLICATION: "detail",
                VIEW_MATERIALS: "materials",
            };
            return actions[this.role.entryAction] || "apply";
        },
        cardActionLabel() {
            if (this.cardAction === "materials") return "我的资料";
            if (this.cardAction === "detail") return "查看审核详情";
            return "提交申请";
        },
        canResubmit() {
            return this.role.entryAction === "VIEW_INITIAL_APPLICATION" &&
                !!this.initialApplication && [3, 4].indexOf(Number(this.initialApplication.applicationStatus)) >= 0;
        },
        cardDescription() {
            if (this.role.entryAction === "VIEW_MATERIALS") {
                if (Number(this.role.currentStatus) === 1) {
                    return "摄影师身份当前已认证；可在我的资料中查看通过资料、维护摄影风格与查看审核记录。";
                }
                return "摄影师基础资料已通过；当前身份不可用时资料仅可查看，不能提交风格更新。";
            }
            if (this.role.entryAction === "VIEW_INITIAL_APPLICATION" && this.initialApplication && Number(this.initialApplication.applicationStatus) === 1) {
                return "首次申请正在审核中，协议材料、风格、设备和证书的处理状态会在此处更新。";
            }
            if (this.role.entryAction === "VIEW_INITIAL_APPLICATION") {
                return "首次申请尚未通过，请在审核详情中查看处理原因并重新提交。";
            }
                return "完成联系方式、协议材料和风格样片后，即可提交摄影师首次申请。";
        },
    },
    watch: {
        initialApplicationId: {
            immediate: true,
            handler() {
                this.applicationDetail = null;
                this.detailError = "";
                this.detailDialogVisible = false;
            },
        },
    },
    methods: {
        handleCardAction() {
            if (this.cardAction === "materials") {
                this.$emit("open-materials");
                return;
            }
            if (this.cardAction === "detail") {
                this.openApplicationDetail();
                return;
            }
            this.dialogVisible = true;
        },
        openApplicationDetail() {
            this.detailDialogVisible = true;
            return this.loadApplicationDetail();
        },
        closeApplicationDetail() {
            this.detailDialogVisible = false;
        },
        openResubmit() {
            this.detailDialogVisible = false;
            this.dialogVisible = true;
        },
        loadApplicationDetail() {
            if (!this.initialApplicationId) {
                this.applicationDetail = null;
                this.detailError = "";
                return Promise.resolve();
            }
            this.detailLoading = true;
            this.detailError = "";
            return request.get(API.creatorCenter.photographer.applicationDetail(this.initialApplicationId))
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.applicationDetail = normalizeProfessionalApplicationDetail(getResultData(response), "photographer");
                })
                .catch((error) => {
                    this.applicationDetail = null;
                    this.detailError = getResultMessage(error, "申请审核详情暂时无法加载，请稍后刷新");
                })
                .finally(() => {
                    this.detailLoading = false;
                });
        },
        handleApplicationSubmitted() {
            this.dialogVisible = false;
            this.detailDialogVisible = false;
            this.$emit("submitted");
        },
    },
};
</script>

<style scoped>
.photographer-application-card { position: relative; display: grid; grid-template-columns: 128px minmax(0, 1fr); gap: 22px; min-height: 272px; padding: 20px; overflow: hidden; background: #fff; border: 1px solid #d8e9f1; border-radius: 24px; box-shadow: 0 14px 30px rgba(65, 101, 120, .08); }.photographer-application-card::before { position: absolute; top: 0; right: 0; left: 0; height: 6px; content: ""; background: #8fd6fa; }.photographer-card-art { height: 230px; min-height: 230px; overflow: hidden; background: #edf8fc; border-radius: 14px; }.photographer-card-portrait { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center; }.photographer-card-content { display: flex; min-width: 0; flex-direction: column; }.photographer-card-topline { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }.photographer-card-topline p { margin: 0 0 3px; color: #5591ae; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.photographer-card-topline h3 { margin: 0; color: #3f3158; font-size: 24px; font-weight: 800; letter-spacing: -.02em; }.photographer-card-badges { display: flex; flex: 0 0 auto; flex-wrap: wrap; justify-content: flex-end; gap: 6px; }.photographer-status-badge, .photographer-campus-badge { display: inline-flex; flex: 0 0 auto; align-items: center; min-height: 26px; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; }.photographer-campus-badge { color: #7957a8; background: #f1eafe; }.photographer-status-badge.ready { color: #347796; background: #e6f5fb; }.photographer-status-badge.review { color: #916022; background: #fff1c9; }.photographer-status-badge.approved { color: #31735a; background: #e6f7ed; }.photographer-status-badge.rejected { color: #a0475c; background: #fff0f3; }.photographer-status-badge.withdrawn { color: #696276; background: #f0eef2; }.photographer-card-description { min-height: 42px; margin: 14px 0 8px; color: #746c7e; font-size: 13px; line-height: 1.65; }.photographer-card-loading, .photographer-card-history, .photographer-card-error { margin: 0 0 14px; color: #857f76; font-size: 12px; line-height: 1.65; }.photographer-card-error { color: #a1495c; }.photographer-review-summary { margin: 0 0 12px; }.photographer-review-head { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: baseline; color: #5e5565; font-size: 12px; }.photographer-review-head strong { color: #4b6f83; }.photographer-review-head span { color: #958d98; font-size: 10px; }.photographer-review-reason { margin: 7px 0 0; color: #a1495c; font-size: 11px; line-height: 1.55; }.photographer-qualification-warning { margin: 8px 0 0; padding: 6px 8px; color: #915e22; background: #fff5dc; border-radius: 7px; font-size: 11px; font-weight: 800; }.photographer-review-group { margin-top: 10px; }.photographer-review-group > strong { color: #716977; font-size: 11px; }.photographer-review-group ul { display: grid; gap: 5px; margin: 5px 0 0; padding: 0; list-style: none; }.photographer-review-group li { display: flex; flex-wrap: wrap; gap: 5px 7px; align-items: baseline; color: #68606d; font-size: 11px; line-height: 1.5; }.photographer-review-group li small { width: 100%; color: #a1495c; font-size: 10px; }.photographer-item-status { display: inline-flex; padding: 1px 5px; border-radius: 4px; font-size: 10px; font-weight: 800; }.photographer-item-status.review { color: #916022; background: #fff1c9; }.photographer-item-status.approved { color: #31735a; background: #e6f7ed; }.photographer-item-status.rejected { color: #a0475c; background: #fff0f3; }.photographer-card-action-row { display: flex; gap: 8px; margin-top: auto; }.photographer-card-action { width: 100%; min-height: 42px; padding: 9px 14px; color: #fff; background: #4f90ae; border: 1px solid #4f90ae; border-radius: 12px; cursor: pointer; font: inherit; font-size: 13px; font-weight: 800; transition: background-color 180ms ease, border-color 180ms ease; }.photographer-card-action:hover { background: #3d7f9d; border-color: #3d7f9d; }.photographer-card-action--quiet { color: #467f9d; background: transparent; border-color: #bbdce9; }.photographer-card-action--quiet:hover { color: #fff; background: #5793b1; border-color: #5793b1; }.photographer-card-action:disabled { cursor: not-allowed; opacity: .65; }
@media (max-width: 1250px) { .photographer-application-card { grid-template-columns: 110px minmax(0, 1fr); }.photographer-card-art { height: 220px; min-height: 220px; } }
.photographer-detail-overlay { position: fixed; z-index: 70; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(32, 27, 41, .56); }.photographer-detail-dialog { display: flex; width: min(560px, calc(100vw - 48px)); max-height: min(680px, calc(100vh - 48px)); flex-direction: column; overflow: hidden; color: #332d49; background: #fff; border: 1px solid #d9e9f1; border-radius: 20px; box-shadow: 0 24px 70px rgba(30, 42, 58, .28); }.photographer-detail-header { display: flex; flex: 0 0 auto; align-items: flex-start; justify-content: space-between; gap: 18px; padding: 19px 22px 16px; border-bottom: 1px solid #e7eff3; }.photographer-detail-header p { margin: 0 0 4px; color: #5591ae; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.photographer-detail-header h4 { margin: 0; color: #3f3158; font-size: 20px; font-weight: 800; }.photographer-detail-header button { display: grid; width: 30px; height: 30px; flex: 0 0 auto; place-items: center; padding: 0; color: #5d6070; background: #f1f6f8; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 22px; line-height: 1; }.photographer-detail-main { min-height: 0; padding: 20px 22px 24px; overflow-y: auto; overscroll-behavior: contain; }.photographer-detail-main .photographer-card-loading, .photographer-detail-main .photographer-card-error { margin: 0; }.photographer-detail-main .photographer-review-summary { margin: 0; }.photographer-detail-dialog button:focus-visible { outline: 0 !important; box-shadow: none !important; }
</style>
